// Add a one-off taxable EMPLOYEE earning (bonus or commission) for the run's
// period. It is added to gross + PAYE and shows as its own payslip line the next
// time the run is (re)validated. Distinct from the non-employee commission rail.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/features/shared/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { oneOffEarningsApi } from '../api/oneOffEarnings';
import { getApiErrorMessage } from '@/lib/apiError';

interface Opt { employeeId: string; fullName: string }

export function OneOffEarningDialog({ open, onClose, employees, periodMonth, currency }: {
  open: boolean; onClose: () => void; employees: Opt[]; periodMonth: string; currency: string;
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [kind, setKind] = useState<'commission' | 'bonus'>('commission');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!employeeId) { toast.error('Choose an employee'); return; }
    if (!amount || Number(amount) <= 0) { toast.error('Enter a positive amount'); return; }
    setSaving(true);
    try {
      await oneOffEarningsApi.stage({ employee_id: employeeId, kind, amount, currency, period_month: periodMonth });
      toast.success(`${kind === 'commission' ? 'Commission' : 'Bonus'} added — re-validate the run to apply it`);
      setAmount('');
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not add the earning'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add one-off earning</DialogTitle>
          <DialogDescription>
            A taxable {kind} for {periodMonth}. It’s added to gross taxable pay and PAYE, and appears as its own payslip line.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder="Select an employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.employeeId} value={e.employeeId}>{e.fullName}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as 'commission' | 'bonus')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="commission">Commission</SelectItem>
                  <SelectItem value="bonus">Bonus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount ({currency})</Label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 300000" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add earning'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
